import { useCallback, useEffect, useRef, useState } from "react";
import {
  fetchAgentRoomState,
  fetchAgentRoomWorkitem,
  postAgentRoomAction,
  type AgentRoomActionPayload,
  type AgentRoomLane,
  type AgentRoomState,
  type AgentRoomWorkitem,
} from "../api";
import { connectOwnerEventStream } from "../eventStream";
import { statusLabel } from "../domain/statusCopy";

/**
 * SIMO-AGENT-ROOM-LIVE-1 — Live-Agent-Room.
 *
 * Live-Daten kommen ausschliesslich aus der echten Supervisor-Projektion
 * (`/api/agent-room/state`); Updates sind event-getrieben (SSE
 * `agent.room.updated`) — im Idle entstehen 0 Modellturns. Controls gehen
 * durch die Work-Authority (POST /api/agent-room/actions), nie in UI-State.
 */

const LANE_ORDER: AgentRoomLane[] = ["ACTIVE", "QUEUED_READY", "WAIT_OWNER", "WAIT_EXTERNAL", "PARKED", "DIAGNOSTICS", "DONE"];

const LANE_LABEL: Record<AgentRoomLane, string> = {
  ACTIVE: "Active",
  QUEUED_READY: "Queued / Ready",
  WAIT_OWNER: "Wait Owner",
  WAIT_EXTERNAL: "Wait External",
  PARKED: "Parked",
  DIAGNOSTICS: "Diagnostics (Tests/Canaries)",
  DONE: "Done",
};

const WAITING_REASON_LABEL: Record<string, string> = {
  WAITING_FOR_OWNER_APPROVAL: "wartet auf Owner-Freigabe",
  WAITING_FOR_RETRY: "wartet auf geplanten Retry",
  WAITING_FOR_EXECUTOR: "Auto-Executor läuft nicht (Systemfehler)",
  WAITING_FOR_PRIMARY_SLOT: "wartet auf freien Primary-Slot",
  WAITING_FOR_RECOVERY: "wartet auf Recovery-Abschluss",
  WAITING_FOR_SCOPE_VERIFICATION: "wartet auf Scope-Verifikation (Scope-Discovery)",
  WAITING_FOR_SCOPE_LEASE: "Writer-Scope ist von anderem Lauf belegt",
  ATTEMPT_BUDGET_EXHAUSTED: "Attempt-Budget erschöpft (nicht claimbar)",
  DISPATCH_PENDING: "Dispatch unmittelbar bevorstehend",
};

function StateBadge({ state, queueState }: { state: string; queueState?: string | null }): JSX.Element {
  //_EMBER_CALM F / §16: Primäres Label aus der kanonischen Feinachse in
  //_Owner-Sprache (z. B. WAIT_EXECUTOR = „Wartet auf einen Agenten"), Rohcode
  //_im title; ohne Feinachse bleibt die Legacy-Achse ehrlich sichtbar.
  const anzeige = queueState ?? state;
  const label = queueState ? (statusLabel(queueState) ?? queueState) : state;
  const tone =
    anzeige === "RUNNING" || anzeige === "ACTIVE"
      ? "border-jarvis-accent/50 bg-jarvis-accent/15 text-jarvis-text"
      : anzeige === "WAIT_OWNER" || anzeige === "WAIT_HUMAN"
        ? "border-red-400/50 bg-red-400/10 text-red-200"
        : anzeige === "WAIT_EXECUTOR" || anzeige === "WAIT_EXTERNAL" || anzeige === "WAIT_RESOURCE" || anzeige === "READY_TO_RESUME" || anzeige === "READY"
          ? "border-sky-400/50 bg-sky-400/10 text-sky-200"
          : anzeige === "TROUBLESHOOTING" || anzeige === "VERIFYING"
            ? "border-orange-400/50 bg-orange-400/10 text-orange-200"
            : anzeige === "PROVEN" || anzeige === "DONE" || anzeige === "COMPLETED"
              ? "border-emerald-400/50 bg-emerald-400/10 text-emerald-200"
              : "border-jarvis-border bg-jarvis-bg/60 text-jarvis-muted";
  return (
    <span
      className={`rounded border px-1.5 py-0.5 text-[10px] uppercase tracking-wide ${tone}`}
      title={anzeige}
    >
      {label}
    </span>
  );
}

function formatAge(iso: string | null): string {
  if (!iso) return "–";
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return "–";
  const minutes = Math.floor(ms / 60000);
  if (minutes < 1) return "gerade eben";
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h ${minutes % 60} min`;
  return `${Math.floor(hours / 24)} d`;
}

/** GAM-01 §8: Warum ein Item gerade nicht vorangeht (Progress-Overlay-Achse). */
const PROGRESS_LABEL: Record<string, string> = {
  RUNNING: "arbeitet",
  WAITING_ON_TOOL: "wartet auf Tool",
  WAITING_ON_SUBAGENT: "wartet auf Subagent",
  WAITING_RESOURCE: "wartet auf Ressource",
  WAITING_OWNER: "wartet auf Owner",
  STALLED: "steckt fest",
  DEGRADED: "degeneriert",
  ROTATING_CONTEXT: "Kontext-Rotation",
  RECOVERING: "in Wiederherstellung",
  DONE: "abgeschlossen",
};

function ProgressBadge({ workitem }: {
  workitem: {
    progressState?: string | null;
    waitReasonDetail?: string | null;
    currentActor?: string | null;
    lastMeaningfulProgressAt?: string | null;
    expectedNextAction?: string | null;
    rotationCount?: number;
  };
}): JSX.Element | null {
  if (!workitem.progressState || workitem.progressState === "RUNNING") return null;
  const warnend = workitem.progressState === "STALLED" || workitem.progressState === "DEGRADED";
  const rotationen = workitem.rotationCount ?? 0;
  const details = [
    workitem.waitReasonDetail ?? null,
    workitem.currentActor ? `Actor: ${workitem.currentActor}` : null,
    workitem.lastMeaningfulProgressAt ? `Fortschritt: vor ${formatAge(workitem.lastMeaningfulProgressAt)}` : null,
    workitem.expectedNextAction ? `Erwartet: ${workitem.expectedNextAction}` : null,
    rotationen > 0 ? `Rotationen: ${rotationen}` : null,
  ].filter((teil) => teil !== null && teil.length > 0).join(" · ");
  return (
    <span
      className={`rounded border px-1.5 py-0.5 text-[10px] font-mono ${warnend ? "border-red-400/60 bg-red-400/10 text-red-200" : "border-sky-400/50 bg-sky-400/10 text-sky-200"}`}
      title={details}
      data-testid="gam-progress-badge"
    >
      {PROGRESS_LABEL[workitem.progressState] ?? workitem.progressState}
      {rotationen > 0 ? ` (R${rotationen})` : ""}
    </span>
  );
}

interface WorkitemDetailProps {
  workitem: AgentRoomWorkitem;
  onAction: (payload: AgentRoomActionPayload) => void;
  onClose: () => void;
}

function WorkitemDetail({ workitem, onAction, onClose }: WorkitemDetailProps): JSX.Element {
  // OWNEXP Red-Team M6: Bestätigungs-Armierung pro Workitem (React-State,
  // keyed — kein Carryover beim Item-Wechsel).
  const [failArmFuer, setFailArmFuer] = useState<string | null>(null);
  const [challengeText, setChallengeText] = useState("");
  // SIMO-AUTONOMY-OPERATIONS-IMPLEMENTATION-1 (P1) — zusaetzliche Anzeige-
  // Ebene: die kanonische Feinachse queue_state (11 Werte) aus der Work-Queue-
  // State-Route. Additiv neben roomState/waitingReason — keine Bestands-
  // Anzeige wird ersetzt oder entfernt.
  const [queueState, setQueueState] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    fetch(`/api/work-queue/${encodeURIComponent(workitem.workitemId)}/state`, { credentials: "same-origin" })
      .then((res) => (res.ok ? (res.json() as Promise<{ queueState?: string | null }>) : null))
      .then((body) => {
        if (!cancelled && body && typeof body.queueState === "string") setQueueState(body.queueState);
      })
      .catch(() => {
        /* unmanaged/fehlerhafte Items bleiben ohne queue_state-Anzeige */
      });
    return () => {
      cancelled = true;
    };
  }, [workitem.workitemId]);
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-2 md:items-center md:p-6" role="dialog" aria-label={`Workitem ${workitem.title}`}>
      <div className="simo-thin-scroll max-h-[85vh] w-full max-w-3xl overflow-y-auto rounded-lg border border-jarvis-border bg-jarvis-surface p-4 shadow-2xl">
        <div className="mb-3 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="mb-1 flex flex-wrap items-center gap-2">
              <StateBadge state={workitem.roomState} queueState={workitem.queueState ?? undefined} />
              <ProgressBadge workitem={workitem} />
              {queueState && (
                <span className="rounded border border-jarvis-accent/40 bg-jarvis-accent/10 px-1.5 py-0.5 text-[10px] font-mono text-jarvis-accent" title="Kanonische Feinachse queue_state (Queue Core P1, 11 Werte)">queue: {queueState}</span>
              )}
              {/* UX-J Red-Team F3: Priorität deutsch (Rohwert im title), nie nackter Code */}
              <span className="rounded border border-jarvis-border bg-jarvis-bg/60 px-1.5 py-0.5 text-[10px] text-jarvis-muted" title={workitem.priority}>
                {workitem.priority === "urgent" ? "Dringend" : workitem.priority === "high" ? "Hoch" : workitem.priority === "low" ? "Niedrig" : "Normal"}
              </span>
              {workitem.recoveryState && (
                <span className="rounded border border-orange-400/50 bg-orange-400/10 px-1.5 py-0.5 text-[10px] font-mono text-orange-200">
                  Recovery: {workitem.recoveryState}
                </span>
              )}
            </div>
            <h2 className="truncate text-sm font-semibold text-jarvis-text">{workitem.title}</h2>
            <p className="mt-0.5 font-mono text-[10px] text-jarvis-muted">{workitem.workitemId}</p>
          </div>
          <button type="button" onClick={onClose} className="rounded border border-jarvis-border px-2 py-1 text-xs text-jarvis-muted hover:text-jarvis-text">
            Schließen
          </button>
        </div>

        <dl className="mb-3 grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs md:grid-cols-3">
          <div><dt className="text-jarvis-muted">Mission</dt><dd className="truncate font-mono text-[11px]">{workitem.missionId ?? "–"}</dd></div>
          <div><dt className="text-jarvis-muted">Recovery für</dt><dd className="truncate font-mono text-[11px]">{workitem.recoveredFromWorkitemId ?? "–"}</dd></div>
          <div><dt className="text-jarvis-muted">Agent / Rolle</dt><dd className="truncate">{workitem.assignedAgent}</dd></div>
          <div><dt className="text-jarvis-muted">Capability</dt><dd>{workitem.modelCapability ?? "auto"}</dd></div>
          <div><dt className="text-jarvis-muted">Provider/Route</dt><dd className="truncate">{workitem.providerRoute ?? "auto"}</dd></div>
          <div><dt className="text-jarvis-muted">Laufzeit</dt><dd>{formatAge(workitem.startedAt)}</dd></div>
          <div><dt className="text-jarvis-muted">Heartbeat</dt><dd>{formatAge(workitem.heartbeatAt)}</dd></div>
          <div><dt className="text-jarvis-muted">Writer/Lease</dt><dd>{workitem.writerLease.leaseExpiresAt ? `bis ${new Date(workitem.writerLease.leaseExpiresAt).toLocaleTimeString()}` : "frei"} · Versuch {workitem.writerLease.attemptCount}/{workitem.writerLease.maxAttempts}</dd></div>
          <div><dt className="text-jarvis-muted">Diagnose-Schritt</dt><dd>{workitem.diagnosticStep}</dd></div>
        </dl>

        <div className="mb-3 rounded border border-jarvis-border bg-jarvis-bg/40 p-2.5 text-xs">
          <div className="mb-1 font-semibold text-jarvis-text">Ziel</div>
          <p className="whitespace-pre-wrap text-jarvis-muted">{workitem.goal}</p>
        </div>

        <div className="mb-3 grid gap-2 text-xs md:grid-cols-2">
          <div className="rounded border border-jarvis-border bg-jarvis-bg/40 p-2.5">
            <div className="mb-1 font-semibold">Nächster Schritt</div>
            <p className="font-mono text-[11px] text-jarvis-muted">{workitem.nextAction ?? "–"}</p>
          </div>
          <div className="rounded border border-jarvis-border bg-jarvis-bg/40 p-2.5">
            <div className="mb-1 font-semibold">Blocker</div>
            <p className="font-mono text-[11px] text-jarvis-muted">{workitem.blocker ?? "keiner"}</p>
          </div>
        </div>

        {workitem.failureHistory.length > 0 && (
          <div className="mb-3 rounded border border-orange-400/40 bg-orange-400/5 p-2.5 text-xs">
            <div className="mb-1.5 font-semibold text-orange-200">Fehler-/Recovery-Historie ({workitem.failureHistory.length})</div>
            <ul className="space-y-1">
              {workitem.failureHistory.map((failure, index) => (
                <li key={`${failure.fingerprint}-${index}`} className="font-mono text-[10px] text-jarvis-muted">
                  {new Date(failure.ts).toLocaleTimeString()} · {failure.errorClass} · {failure.disposition}
                  {failure.diagnosticStep > 0 ? ` · Diagnose-Schritt ${failure.diagnosticStep}` : ""} · fp {failure.fingerprint.slice(0, 12)}
                </li>
              ))}
            </ul>
          </div>
        )}

        {(workitem.runs?.length ?? 0) > 0 && (
          <details open className="mb-3 rounded border border-jarvis-border bg-jarvis-bg/40 p-2.5 text-xs">
            <summary className="cursor-pointer font-semibold">Echte Runs / Subagents ({workitem.runs.length})</summary>
            <ul className="mt-1.5 space-y-1">
              {workitem.runs.map((run) => (
                <li key={run.runId} className="font-mono text-[10px] text-jarvis-muted">
                  <span className={run.state === "RUNNING" ? "text-green-300" : run.state === "ERROR" ? "text-red-300" : run.state === "DONE" ? "text-jarvis-muted" : "text-yellow-300"}>{run.state}</span>
                  {" "}{run.role} · {run.resolvedModel ?? run.modelCapability ?? "auto"} · Turns {run.turnCount} · Stage {run.currentStage ?? "–"}
                  <br />{run.runId}{run.parentRunId ? ` ← parent ${run.parentRunId}` : ""} · HB {run.heartbeatAt ? formatAge(run.heartbeatAt) : "–"}
                  {run.errorSummary ? <><br />Fehler: {run.errorSummary.slice(0, 140)}</> : null}
                </li>
              ))}
            </ul>
          </details>
        )}

        {workitem.evidence.length > 0 && (
          <details className="mb-3 rounded border border-jarvis-border bg-jarvis-bg/40 p-2.5 text-xs">
            <summary className="cursor-pointer font-semibold">Evidence ({workitem.evidence.length})</summary>
            <ul className="mt-1.5 space-y-1">
              {workitem.evidence.slice(-10).map((entry, index) => (
                <li key={index} className="truncate font-mono text-[10px] text-jarvis-muted">{JSON.stringify(entry)}</li>
              ))}
            </ul>
          </details>
        )}

        <div className="mb-3 rounded border border-jarvis-border bg-jarvis-bg/40 p-2.5">
          <div className="mb-1.5 text-xs font-semibold">Challenge</div>
          <div className="flex gap-2">
            <input
              value={challengeText}
              onChange={(event) => setChallengeText(event.target.value)}
              placeholder="Claim challengen: Was ist unbewiesen?"
              className="min-w-0 flex-1 rounded border border-jarvis-border bg-jarvis-bg px-2 py-1.5 text-xs"
              aria-label="Challenge-Claim"
            />
            <button
              type="button"
              disabled={!challengeText.trim()}
              onClick={() => {
                onAction({ type: "challenge", workitemId: workitem.workitemId, claim: challengeText.trim() });
                setChallengeText("");
              }}
              className="rounded border border-jarvis-accent/50 bg-jarvis-accent/20 px-3 py-1.5 text-xs disabled:opacity-40"
            >
              CHALLENGE
            </button>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 text-xs">
          <button type="button" onClick={() => onAction({ type: "park", workitemId: workitem.workitemId })} className="rounded border border-jarvis-border px-3 py-1.5 hover:bg-jarvis-bg/60">PARK</button>
          <button type="button" onClick={() => onAction({ type: "resume", workitemId: workitem.workitemId })} className="rounded border border-jarvis-border px-3 py-1.5 hover:bg-jarvis-bg/60">RESUME</button>
          <button type="button" onClick={() => onAction({ type: "priority", workitemId: workitem.workitemId, priority: "urgent" })} className="rounded border border-jarvis-border px-3 py-1.5 hover:bg-jarvis-bg/60">PRIORITY: URGENT</button>
          <button type="button" onClick={() => onAction({ type: "priority", workitemId: workitem.workitemId, priority: "normal" })} className="rounded border border-jarvis-border px-3 py-1.5 hover:bg-jarvis-bg/60">PRIORITY: NORMAL</button>
          <button type="button" onClick={() => onAction({ type: "safe_cancel", workitemId: workitem.workitemId })} className="rounded border border-red-400/40 px-3 py-1.5 text-red-200 hover:bg-red-400/10">SAFE CANCEL</button>
          {workitem.ownerGate?.active === true && (
            <>
              <button type="button" onClick={() => onAction({ type: "owner_decision", workitemId: workitem.workitemId, decision: "approve" })} className="rounded border border-emerald-400/50 bg-emerald-400/10 px-3 py-1.5 text-emerald-200">OWNER: APPROVE</button>
              <button type="button" onClick={() => onAction({ type: "owner_decision", workitemId: workitem.workitemId, decision: "reject" })} className="rounded border border-red-400/50 px-3 py-1.5 text-red-200">OWNER: REJECT</button>
            </>
          )}
          {workitem.roomState === "WAIT_OWNER" && workitem.ownerGate?.active !== true && (
            <span className="rounded border border-orange-400/40 bg-orange-400/10 px-3 py-1.5 text-orange-200">
              SIMO arbeitet automatisch weiter — technischer Zustand, keine Owner-Entscheidung nötig.
            </span>
          )}
          <select
            aria-label="Capability Override"
            defaultValue={workitem.modelCapability ?? ""}
            onChange={(event) => {
              const value = event.target.value as "glm.fast" | "glm.default" | "glm.reasoning" | "";
              if (value) onAction({ type: "capability_override", workitemId: workitem.workitemId, capability: value });
            }}
            className="rounded border border-jarvis-border bg-jarvis-bg px-2 py-1.5"
          >
            <option value="">Capability: auto</option>
            <option value="glm.fast">glm.fast</option>
            <option value="glm.default">glm.default</option>
            <option value="glm.reasoning">glm.reasoning</option>
          </select>
          {workitem.recoveryState && (
            <button type="button" onClick={() => onAction({ type: "advance_recovery", workitemId: workitem.workitemId })} className="rounded border border-orange-400/50 bg-orange-400/10 px-3 py-1.5 text-orange-200">
              RECOVERY SCHRITT →
            </button>
          )}
          {/* OWNEXP Red-Team M6: echte Recovery-Leiter — KEIN Ein-Klick-Fehler-
              injizieren. Doppelklick-Bestätigung als React-State, keyed nach
              Workitem (kein Carryover auf ein anderes Item). */}
          <button
            type="button"
            title="Simuliert einen bounded Fehler, um Troubleshooting statt Blind-Retry zu zeigen — versetzt das Item in die echte Recovery-Leiter (Bestätigung erforderlich)"
            onClick={() => {
              if (failArmFuer !== workitem.workitemId) {
                setFailArmFuer(workitem.workitemId);
                window.setTimeout(() => setFailArmFuer((fuer) => (fuer === workitem.workitemId ? null : fuer)), 4000);
                return;
              }
              setFailArmFuer(null);
              onAction({ type: "report_failure", workitemId: workitem.workitemId, errorClass: "test_bounded_failure", action: "agent-room.ui.failure_probe", stateHash: `ui-${new Date().toISOString().slice(0, 13)}` });
            }}
            className="rounded border border-jarvis-border px-3 py-1.5 hover:bg-jarvis-bg/60"
          >
            {failArmFuer === workitem.workitemId ? "WIRKLICH? ERNEUT KLICKEN" : "FEHLER SIMULIEREN"}
          </button>
        </div>
      </div>
    </div>
  );
}

function WorkitemCard({ workitem, onOpen }: { workitem: AgentRoomWorkitem; onOpen: () => void }): JSX.Element {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="w-full rounded-lg border border-jarvis-border bg-jarvis-bg/40 p-2.5 text-left transition-colors hover:border-jarvis-accent/40 hover:bg-jarvis-bg/70"
    >
      <div className="mb-1 flex flex-wrap items-center gap-1.5">
        <StateBadge state={workitem.roomState} queueState={workitem.queueState ?? undefined} />
              <ProgressBadge workitem={workitem} />
        {workitem.isRecoveryItem && <span className="rounded border border-orange-400/50 bg-orange-400/10 px-1.5 py-0.5 text-[10px] font-mono text-orange-200">RECOVERY</span>}
        {workitem.isChallengeItem && <span className="rounded border border-sky-400/50 bg-sky-400/10 px-1.5 py-0.5 text-[10px] font-mono text-sky-200">CHALLENGE</span>}
        {workitem.recoveryState && <span className="font-mono text-[10px] text-orange-200">{workitem.recoveryState}</span>}
        <span className="ml-auto font-mono text-[10px] text-jarvis-muted">{formatAge(workitem.heartbeatAt)}</span>
      </div>
      <div className="truncate text-xs font-medium text-jarvis-text">{workitem.title}</div>
      <div className="mt-0.5 flex flex-wrap items-center gap-1">
        <AgentBadge agent={workitem.assignedAgent} />
      </div>
      {workitem.nextAction && <div className="mt-1 truncate font-mono text-[10px] text-jarvis-accent/80">→ {workitem.nextAction}</div>}
      {workitem.waitingReason && (
        <div className="mt-1 truncate font-mono text-[10px] text-yellow-300/90">
          ⏳ {workitem.waitingReason} — {WAITING_REASON_LABEL[workitem.waitingReason] ?? ""}
        </div>
      )}
      {workitem.blocker && <div className="mt-0.5 truncate font-mono text-[10px] text-red-300/80">⛔ {workitem.blocker}</div>}
    </button>
  );
}

export function AgentRoomPanel(): JSX.Element {
  const [state, setState] = useState<AgentRoomState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<AgentRoomWorkitem | null>(null);
  const [delegationGoal, setDelegationGoal] = useState("");
  const [delegationCapability, setDelegationCapability] = useState("");
  const [delegationPriority, setDelegationPriority] = useState("normal");
  const [actionFeedback, setActionFeedback] = useState<string | null>(null);
  // §9: sofortiger Delegations-Receipt — der Owner darf die Queue nicht
  // durchsuchen muessen, um zu wissen, ob der Klick gewirkt hat.
  const [delegationReceipt, setDelegationReceipt] = useState<{ workitemId: string; title: string; priority: string; at: string } | null>(null);
  const [live, setLive] = useState<boolean | null>(null); // null = verbinde, true = live, false = gestört
  // CP-11 — Operator-Filter (Mission/Agent/Titel, nur Aktion/Recovery/Challenge).
  const [filterText, setFilterText] = useState("");
  const [nurAktionen, setNurAktionen] = useState(false);
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const refresh = useCallback(async () => {
    try {
      const next = await fetchAgentRoomState();
      setState(next);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  const refreshSelected = useCallback(async (id: string) => {
    try {
      const detail = await fetchAgentRoomWorkitem(id);
      setSelected(detail.workitem);
    } catch {
      /* Detail-Refresh best-effort; Snapshot-Refresh folgt via SSE */
    }
  }, []);

  // Initial-Snapshot + event-getriebene Live-Updates (kein Polling, 0 Modellturns im Idle).
  useEffect(() => {
    void refresh();
    const handle = connectOwnerEventStream({
      onOpen: () => {
        setLive(true);
        void refresh();
      },
      // OWNEXP Red-Team H3: LIVE-Badge spiegelt die Verbindungsrealität —
      // nach Stream-Fehler zeigt die Fläche OFFEN(?) statt eingefrorenem LIVE.
      onError: () => setLive(false),
      onEvent: (event) => {
        if (event.type !== "agent.room.updated") return;
        if (refreshTimer.current) clearTimeout(refreshTimer.current);
        refreshTimer.current = setTimeout(() => {
          void refresh();
        }, 150);
      },
    });
    return () => {
      handle.close();
      if (refreshTimer.current) clearTimeout(refreshTimer.current);
    };
  }, [refresh]);

  const runAction = useCallback(
    async (payload: AgentRoomActionPayload) => {
      try {
        const result = await postAgentRoomAction(payload);
        if (result.ok) {
          setActionFeedback(`${String(payload.type)} ✓${result.workitemId ? ` (${result.workitemId.slice(0, 8)})` : ""}`);
          if (payload.type === "delegate" && result.workitemId) {
            const detail = (result.detail ?? {}) as { title?: string };
            setDelegationReceipt({
              workitemId: result.workitemId,
              title: detail.title ?? String(payload.goal).split("\n")[0] ?? "",
              priority: typeof payload.priority === "string" ? payload.priority : "normal",
              at: new Date().toISOString(),
            });
          }
        } else {
          setActionFeedback(`${String(payload.type)} ✗ ${result.error ?? "unbekannter Fehler"}`);
        }
      } catch (err) {
        setActionFeedback(`${String(payload.type)} ✗ ${err instanceof Error ? err.message : String(err)}`);
      }
      await refresh();
      const workitemId = typeof payload.workitemId === "string" ? payload.workitemId : null;
      if (workitemId && (selected?.workitemId === workitemId || payload.type === "advance_recovery")) {
        await refreshSelected(workitemId);
      }
    },
    [refresh, refreshSelected, selected],
  );

  const openDetail = useCallback(
    (workitem: AgentRoomWorkitem) => {
      setSelected(workitem);
      void refreshSelected(workitem.workitemId);
    },
    [refreshSelected],
  );

  const global = state?.global;

  return (
    <div className="simo-thin-scroll h-full min-h-0 overflow-y-auto p-3 md:p-4">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <h1 className="text-sm font-semibold text-jarvis-text">Agent Room</h1>
        <span className={`status-dot ${live === true ? "simo-accent-ok" : "simo-accent-warn"}`} style={{ width: 8, height: 8 }} aria-hidden="true" />
        <span className="text-[10px] font-mono text-jarvis-muted">{live === true ? "LIVE (event-getrieben)" : live === false ? "Verbindung gestört — Daten können veraltet sein" : "verbinde…"}</span>
        {state && <span className="ml-auto font-mono text-[10px] text-jarvis-muted">Stand {new Date(state.generatedAt).toLocaleTimeString()}</span>}
      </div>

      {error && (
        <div className="mb-3 rounded border border-red-400/50 bg-red-400/10 p-2.5 text-xs text-red-200">
          Projektion nicht verfügbar: {error}
        </div>
      )}
      {actionFeedback && (
        <div className="mb-3 rounded border border-jarvis-accent/40 bg-jarvis-accent/10 p-2.5 font-mono text-xs text-jarvis-text">{actionFeedback}</div>
      )}

      {delegationReceipt && global && (() => {
        const all = Object.values(state?.lanes ?? {}).flat();
        const item = all.find((w) => w.workitemId === delegationReceipt.workitemId);
        const running = state?.lanes.ACTIVE.some((w) => w.workitemId === delegationReceipt.workitemId) ?? false;
        return (
          <div className="mb-3 rounded-lg border border-emerald-400/50 bg-emerald-400/10 p-3" role="status">
            <div className="text-xs font-semibold text-emerald-200">AUFTRAG ÜBERNOMMEN</div>
            <div className="mt-1 truncate text-xs text-jarvis-text">{delegationReceipt.title}</div>
            <dl className="mt-1.5 grid grid-cols-2 gap-x-4 gap-y-1 font-mono text-[10px] text-jarvis-muted md:grid-cols-4">
              <div><dt className="inline text-jarvis-muted">Priorität: </dt><dd className="inline text-jarvis-text">{delegationReceipt.priority}</dd></div>
              <div><dt className="inline text-jarvis-muted">Status: </dt><dd className="inline text-jarvis-text">{item?.roomState ?? "READY"}</dd></div>
              <div><dt className="inline text-jarvis-muted">Warum: </dt><dd className="inline text-jarvis-text">{item?.waitingReason ? WAITING_REASON_LABEL[item.waitingReason] ?? item.waitingReason : running ? "wird ausgeführt" : "in Queue"}</dd></div>
              <div><dt className="inline text-jarvis-muted">Nächster Schritt: </dt><dd className="inline text-jarvis-text">{item?.nextAction ?? (running ? "Agent läuft" : "Auto-Executor-Claim")}</dd></div>
            </dl>
            <div className="mt-1 font-mono text-[10px] text-jarvis-muted">Owner-Aktion: keine.</div>
            <button type="button" onClick={() => setDelegationReceipt(null)} className="mt-1.5 rounded border border-jarvis-border px-2 py-0.5 text-[10px] text-jarvis-muted hover:text-jarvis-text">OK</button>
          </div>
        );
      })()}

      {global && (
        <div className="mb-3 grid grid-cols-2 gap-2 md:grid-cols-4">
          <div className="rounded-lg border border-jarvis-border bg-jarvis-surface/70 p-2.5">
            <div className="text-[10px] uppercase tracking-wide text-jarvis-muted">Budget / Route</div>
            <div className="mt-1 truncate text-xs">{global.budgetState.route}</div>
            <div className="font-mono text-[10px] text-jarvis-muted">Pool {global.budgetState.pool} · 5h {global.budgetState.window5h} · weekly {global.budgetState.weekly}</div>
          </div>
          <div className="rounded-lg border border-jarvis-border bg-jarvis-surface/70 p-2.5">
            <div className="text-[10px] uppercase tracking-wide text-jarvis-muted">Slots</div>
            <div className="mt-1 text-xs">
              Aktiv: {global.slots.primaryInUse} Primary · {global.slots.reviewInUse} Review
            </div>
            <div className="text-xs text-jarvis-muted">
              Verfügbar: {global.slots.availablePrimary ?? "?"} Primary
              {global.slots.maxPrimarySlots !== null ? ` von ${global.slots.maxPrimarySlots}` : ""}
            </div>
            <div className="font-mono text-[10px] text-jarvis-muted">{global.slots.note}</div>
          </div>
          <div className="rounded-lg border border-jarvis-border bg-jarvis-surface/70 p-2.5">
            <div className="text-[10px] uppercase tracking-wide text-jarvis-muted">Auto-Executor</div>
            <div className={`mt-1 text-xs ${global.autoExecutor.running ? "text-emerald-300" : "text-red-300"}`}>
              {global.autoExecutor.running ? "RUNNING" : global.autoExecutor.configured ? "GESTOPPT" : "NICHT KONFIGURIERT"}
            </div>
            <div className="font-mono text-[10px] text-jarvis-muted">
              Letzter Tick {global.autoExecutor.lastTickAt ? new Date(global.autoExecutor.lastTickAt).toLocaleTimeString() : "–"}
              {global.autoExecutor.lastTickStatus ? ` · ${global.autoExecutor.lastTickStatus}` : ""}
            </div>
          </div>
          <div className="rounded-lg border border-jarvis-border bg-jarvis-surface/70 p-2.5">
            <div className="text-[10px] uppercase tracking-wide text-jarvis-muted">Lanes</div>
            <div className="mt-1 text-xs">Offen {global.openWorkitems} · Aktiv {global.activeWorkitems} · Troubleshooting {global.troubleshooting}</div>
            <div className="font-mono text-[10px] text-jarvis-muted">WAIT_OWNER {global.waitOwner} · WAIT_EXTERNAL {global.waitExternal} · PARKED {global.parked}</div>
          </div>
          <div className="rounded-lg border border-jarvis-border bg-jarvis-surface/70 p-2.5">
            <div className="text-[10px] uppercase tracking-wide text-jarvis-muted">Provider Health</div>
            <ul className="mt-1 space-y-0.5">
              {global.providerHealth.map((provider) => (
                <li key={provider.surface} className="truncate font-mono text-[10px]">
                  <span className={provider.status === "up" ? "text-emerald-300" : "text-jarvis-muted"}>{provider.status === "up" ? "●" : "○"}</span> {provider.surface}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <div className="mb-4 rounded-lg border border-jarvis-accent/30 bg-jarvis-surface/70 p-3">
        <div className="mb-2 text-xs font-semibold text-jarvis-text">Delegieren</div>
        <div className="flex flex-col gap-2 md:flex-row">
          <input
            value={delegationGoal}
            onChange={(event) => setDelegationGoal(event.target.value)}
            placeholder='z. B. "Arbeite HAMTEC B2 weiter" oder "Prüfe den Druckerfehler"'
            className="min-w-0 flex-1 rounded border border-jarvis-border bg-jarvis-bg px-3 py-2 text-xs"
            aria-label="Delegations-Ziel"
          />
          <select value={delegationCapability} onChange={(event) => setDelegationCapability(event.target.value)} aria-label="Capability" className="rounded border border-jarvis-border bg-jarvis-bg px-2 py-2 text-xs">
            <option value="">auto</option>
            <option value="glm.fast">glm.fast</option>
            <option value="glm.default">glm.default</option>
            <option value="glm.reasoning">glm.reasoning</option>
          </select>
          <select value={delegationPriority} onChange={(event) => setDelegationPriority(event.target.value)} aria-label="Priorität" className="rounded border border-jarvis-border bg-jarvis-bg px-2 py-2 text-xs">
            <option value="urgent">urgent</option>
            <option value="high">high</option>
            <option value="normal">normal</option>
            <option value="low">low</option>
          </select>
          <button
            type="button"
            disabled={!delegationGoal.trim()}
            onClick={() => {
              void runAction({
                type: "delegate",
                goal: delegationGoal.trim(),
                priority: delegationPriority,
                ...(delegationCapability
                  ? { capability: delegationCapability as "glm.fast" | "glm.default" | "glm.reasoning" }
                  : {}),
              });
              setDelegationGoal("");
            }}
            className="rounded border border-jarvis-accent/50 bg-jarvis-accent/20 px-4 py-2 text-xs font-semibold disabled:opacity-40"
          >
            DELEGATE TASK
          </button>
          <button
            type="button"
            disabled={!delegationGoal.trim()}
            onClick={() => {
              void runAction({ type: "new_mission", goal: delegationGoal.trim(), priority: delegationPriority });
              setDelegationGoal("");
            }}
            className="rounded border border-jarvis-border px-4 py-2 text-xs disabled:opacity-40"
          >
            NEW MISSION
          </button>
        </div>
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-2 text-xs" data-testid="agent-room-filter" aria-label="Agent-Room Filter">
        <input className="rounded border border-jarvis-border bg-jarvis-bg px-2 py-1.5 min-w-[14rem] flex-1" placeholder="Filter: Titel, Agent, Mission, Workitem-Id…" value={filterText} onChange={(e) => setFilterText(e.target.value)} aria-label="Agent-Room Suche" />
        <label className="flex items-center gap-1.5 text-jarvis-muted">
          <input type="checkbox" checked={nurAktionen} onChange={(e) => setNurAktionen(e.target.checked)} />
          nur Aktion/Recovery/Challenge
        </label>
      </div>

      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {LANE_ORDER.map((lane) => (
          <section key={lane} className="min-w-0 rounded-lg border border-jarvis-border bg-jarvis-surface/50 p-2.5" aria-label={`Lane ${LANE_LABEL[lane]}`}>
            <div className="mb-2 flex items-center gap-2">
              <h2 className="text-xs font-semibold text-jarvis-text">{LANE_LABEL[lane]}</h2>
              <span className="rounded border border-jarvis-border px-1.5 text-[10px] font-mono text-jarvis-muted">{state?.lanes[lane]?.length ?? 0}</span>
            </div>
            <div className="space-y-2">
              {(state?.lanes[lane] ?? []).filter((workitem) => {
                const q = filterText.trim().toLowerCase();
                if (q) {
                  const heu = `${workitem.workitemId} ${workitem.title} ${workitem.assignedAgent} ${workitem.missionId ?? ""}`.toLowerCase();
                  if (!heu.includes(q)) return false;
                }
                if (nurAktionen && !workitem.isRecoveryItem && !workitem.isChallengeItem && workitem.roomState !== "WAIT_OWNER" && workitem.roomState !== "TROUBLESHOOTING") return false;
                return true;
              }).map((workitem) => (
                <WorkitemCard key={workitem.workitemId} workitem={workitem} onOpen={() => openDetail(workitem)} />
              ))}
              {(state?.lanes[lane] ?? []).length === 0 && <p className="px-1 py-2 text-[11px] text-jarvis-muted">leer</p>}
            </div>
          </section>
        ))}
      </div>

      {selected && (
        <WorkitemDetail
          workitem={selected}
          onAction={(payload) => void runAction(payload)}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}

/** CP-11 — distinkte Akteur-Darstellung: Rollen lesen sich auf einen Blick,
 *  menschliche Akteure anders markiert als Maschinen-Akteure. */
function AgentBadge({ agent }: { agent: string }): JSX.Element {
  const name = agent.trim() || "unassigned";
  const istMensch = /^(owner|mensch|human|ibrahim)/iu.test(name);
  const klasse = istMensch
    ? "border-amber-400/50 bg-amber-400/10 text-amber-200"
    : /supervisor/iu.test(name)
      ? "border-violet-400/50 bg-violet-400/10 text-violet-200"
      : /verifier/iu.test(name)
        ? "border-emerald-400/50 bg-emerald-400/10 text-emerald-200"
        : /red.?team|challenger/iu.test(name)
          ? "border-rose-400/50 bg-rose-400/10 text-rose-200"
          : /explorer/iu.test(name)
            ? "border-sky-400/50 bg-sky-400/10 text-sky-200"
            : "border-jarvis-border bg-jarvis-bg/60 text-jarvis-muted";
  return (
    <span className={"rounded border px-1.5 py-0.5 font-mono text-[10px] " + klasse} title={"Akteur: " + name}>
      {name}
    </span>
  );
}
